import { createContext, type ReactNode, useContext, useMemo } from "react";

import type { Option } from "../constants";
import { formatScreenFieldDisplayLabel } from "../utils/resource-insert-label-utils";
import type { ScreenFieldRef } from "../utils/data-field-type-utils";

type RuleScreenFieldDisplayContextValue = {
	currentScreenFieldIds: ReadonlySet<string>;
	ruleLocationName?: string;
	formatScreenFieldLabel: (
		label: string,
		fieldId?: string,
		screenFieldRef?: ScreenFieldRef,
	) => string;
};

const RuleScreenFieldDisplayContext = createContext<RuleScreenFieldDisplayContextValue | null>(
	null,
);

export function RuleScreenFieldDisplayProvider({
	ruleFieldOptions,
	ruleLocationName,
	children,
}: {
	ruleFieldOptions: Option[];
	ruleLocationName?: string;
	children: ReactNode;
}) {
	const value = useMemo((): RuleScreenFieldDisplayContextValue => {
		const currentScreenFieldIds = new Set(ruleFieldOptions.map((o) => o.value));
		return {
			currentScreenFieldIds,
			ruleLocationName,
			formatScreenFieldLabel: (
				label: string,
				fieldId?: string,
				screenFieldRef?: ScreenFieldRef,
			) =>
				formatScreenFieldDisplayLabel(label, fieldId, currentScreenFieldIds, {
					screenFieldRef,
					ruleLocationName,
				}),
		};
	}, [ruleFieldOptions, ruleLocationName]);

	return (
		<RuleScreenFieldDisplayContext.Provider value={value}>
			{children}
		</RuleScreenFieldDisplayContext.Provider>
	);
}

export function useRuleScreenFieldDisplay(): RuleScreenFieldDisplayContextValue | null {
	return useContext(RuleScreenFieldDisplayContext);
}
